import Swiper from 'swiper'
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules'
import { getProductRecommendations } from 'utils/requests'
import { imagesLoaded, handle } from 'utils/gw'
import { formatPrice } from 'utils/general'
import { NostoHelper } from 'helpers/nosto'
import { lowPriority } from 'lib/performance'
import { enhanceSwiperAccessibility } from 'utils/swiper-accessibility'

if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    freshProductCount = 0

    constructor() {
      super()
      this.productId = this.getAttribute('product-id')
      this.nostoPlacementId = this.getAttribute('nosto-placement-id') || false
      this.isCartDrawer = this.getAttribute('id') === 'CartDrawerRecommendations'
      this.searchQuery = ''
      this.productSlides = []
      // this.trigger =
      //   this.getAttribute('load-on') ?? this.isCartDrawer ? 'cart-open' : 'init'
      this.trigger = 'init'
      this.cardTemplate = this.querySelector('template')?.content?.firstElementChild
      this.groupSlides = this.getAttribute('group-slides') === 'true'

      this.patterns = {
        arrayIndex: /\[([0-9]+)\]/,
      }

      this.container = this.closest('[data-recommendations-container]') ?? this

      this.init()
    }

    get storageId() {
      this.storedMarkupId ??= `product-recommendations${this.nostoPlacementId ? `-${this.nostoPlacementId}` : ''}${
        this.productId ? `-${this.productId}` : ''
      }`
      return this.storedMarkupId
    }

    get storedRecommendations() {
      this.storedMarkup ??= localStorage.getItem(this.storageId)
      return this.storedMarkup
    }

    set storedRecommendations(markup) {
      this.storedMarkup = markup
      localStorage.setItem(this.storageId, markup)
    }

    get hasRecommendations() {
      return this.wrapper.childNodes.length > 0
    }

    init() {
      // TODO: Remove on next push
      if (this.storedRecommendations) {
        const storedRecommendationKeys = Object.keys(localStorage).filter(key =>
          key.startsWith('product-recommendations')
        )
        for (const key of storedRecommendationKeys) {
          localStorage.removeItem(key)
        }

        // console.debug(
        //   'Loading recommendations from storage for nosto placement ID:',
        //   this.nostoPlacementId
        // )
        // this.wrapper.innerHTML = this.storedRecommendations
      }

      switch (this.trigger) {
        case 'init':
          this.load()
          break
        case 'cart-open':
          document.addEventListener('MINI_CART_TOGGLED', this.load.bind(this), {
            once: true,
          })
          break
        case 'intersect':
        case 'intersection':
          this.initObserver()
          break
        case 'idle':
          lowPriority(this.load.bind(this))
          break
      }
    }

    loadOnIntersection(entries, observer) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.load()
          observer.disconnect()
        }
      })
    }

    initObserver() {
      const offset = this.getAttribute('intersection-offset') || '200%'
      this.handleIntersection = this.loadOnIntersection.bind(this)
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: `0px 0px ${offset} 0px`,
      })

      this.observer.observe(this)
    }

    async load() {
      if (this.hasRecommendations) this.initSlider()

      console.debug('Fetching product recommendations...')
      if (this.nostoPlacementId) {
        this.waitForNostoToLoad()
      } else {
        try {
          const response = await fetch(this.dataset.url)
          const responseText = await response.text()
          const html = document.createElement('div')
          html.innerHTML = responseText
          const recommendations = html.querySelector('product-recommendations')
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML
          }

          this.initSlider()
        } catch (e) {
          console.error('Failed to fetch product recommendations', e)
        }
      }
    }

    async getProduct(url) {
      url = url.replace('https://brochuwalker.myshopify.com', 'https://www.brochuwalker.com')
      return await fetch(`${url}?section_id=api-product-card`).then(async response => {
        const product = await response.text()
        const fresh = document.createElement('div')
        fresh.innerHTML = product

        return `<div class="swiper-slide">${
          fresh.querySelector('[data-api-content]').firstElementChild.innerHTML
        }</div>`
      })
    }

    get wrapper() {
      this.sliderWrapper = this.sliderWrapper ?? this.querySelector(`.product-recommendations-slider .swiper-wrapper`)

      return this.sliderWrapper
    }

    productCardMarkup(product) {
      const productCard = this.cardTemplate.cloneNode(true)

      productCard.setAttribute('x-nosto-handle', product.handle)

      const conditionalElements = [...productCard.querySelectorAll('[data-if]')].filter(
        el => !el.closest('[data-template="loop"]')
      )
      for (const element of conditionalElements) {
        let condition = element.dataset.if
        const inverse = condition.startsWith('!')
        let index
        if (inverse) {
          condition = condition.replace('!', '')
        }
        const conditionKeyMatch = condition.match(this.patterns.arrayIndex)
        if (conditionKeyMatch) {
          condition = condition.replace(conditionKeyMatch[0], '')
          index = conditionKeyMatch[1]
        }

        let isConditionMet = false

        if (
          (typeof index === 'undefined' && product[condition]) ||
          (typeof index !== 'undefined' && product[condition]?.[index])
        ) {
          isConditionMet = true
        }

        isConditionMet = inverse ? !isConditionMet : isConditionMet

        if (!isConditionMet) {
          element.remove()
        }
      }

      let cardContentMarkup = productCard.innerHTML

      for (const key of Object.keys(product)) {
        const replace = product[key]

        const search = `[[${key}]]`
        if (Array.isArray(replace)) {
          for (const index in replace) {
            const itemReplace = replace[index]

            if (!['string', 'number'].includes(typeof itemReplace)) continue

            const itemSearch = search.replace(']]', `[${index}]]]`)
            cardContentMarkup = cardContentMarkup.replaceAll(itemSearch, itemReplace)
          }

          continue
        }

        if (!['string', 'number'].includes(typeof replace)) continue

        cardContentMarkup = cardContentMarkup.replaceAll(search, replace)
      }

      productCard.innerHTML = cardContentMarkup

      const replacementElements = [...productCard.querySelectorAll('[data-template-element]')]
      for (const el of replacementElements) {
        const elementType = el.dataset.templateElement
        const element = document.createElement(elementType)
        for (const attr of el.attributes) {
          element.setAttribute(attr.name, attr.value)
        }
        element.innerHTML = el.innerHTML
        el.replaceWith(element)
      }

      const conditionalClassElements = [...productCard.querySelectorAll('[data-conditional-classes]')]
      for (const el of conditionalClassElements) {
        const conditionalClasses = JSON.parse(el.dataset.conditionalClasses)

        for (let [condition, classes] of Object.entries(conditionalClasses)) {
          if (!Array.isArray(classes)) classes = [classes]
          const inverse = condition.startsWith('!')
          let index
          if (inverse) {
            condition = condition.replace('!', '')
          }
          const conditionKeyMatch = condition.match(this.patterns.arrayIndex)
          if (conditionKeyMatch) {
            condition = condition.replace(conditionKeyMatch[0], '')
            index = conditionKeyMatch[1]
          }

          let isConditionMet = false

          if (
            (typeof index === 'undefined' && product[condition]) ||
            (typeof index !== 'undefined' && product[condition]?.[index])
          ) {
            isConditionMet = true
          }

          isConditionMet = inverse ? !isConditionMet : isConditionMet

          if (isConditionMet) {
            el.classList.add(...classes)
          }
        }
      }

      return productCard
    }

    async loopProducts(realSlotIdRendered, products, count = 0, shown = 0) {
      count = count ? count : products.length - 1
      if (shown == 0) {
        this.productSlides = []
        if (!this.hasRecommendations) {
          this.wrapper.style.display = 'none'
          this.wrapper.style.opacity = 0
        }
      }
      if (shown <= count) {
        const product = products[shown]

        const slide = document.createElement('div')
        slide.classList.add('swiper-slide')
        slide.appendChild(this.productCardMarkup(product))

        this.productSlides.push(slide)

        lowPriority(() => {
          const url = product.url.replace('https://brochuwalker.myshopify.com', 'https://www.brochuwalker.com')

          fetch(`${url}?section_id=api-product-card`).then(async response => {
            const product = await response.text()
            const fresh = document.createElement('div')
            fresh.innerHTML = product

            fresh
              .querySelector('.js-anchor')
              .setAttribute(
                'href',
                `${fresh.querySelector('.js-anchor').getAttribute('href')}/?nosto=${realSlotIdRendered}`
              )

            fresh
              .querySelector('.card-information__wrapper > a')
              .setAttribute(
                'href',
                `${fresh
                  .querySelector('.card-information__wrapper > a')
                  .getAttribute('href')}/?nosto=${realSlotIdRendered}`
              )

            await imagesLoaded(fresh, { container: true })

            slide.replaceChildren(fresh.querySelector('[data-api-content]').firstElementChild)
            this.freshProductCount++

            if (this.freshProductCount === products.length) {
              NostoHelper.triggerMigration(`#${this.id}`, true)
            }
          })
        }, 0)

        shown++
        this.loopProducts(realSlotIdRendered, products, count, shown)
      } else {
        await this.updateRecommendations()
      }
    }

    async updateRecommendations() {
      // const hasCachedRecommendations = this.hasRecommendations

      // if (hasCachedRecommendations) {
      //   await imagesLoaded(this.productSlides, { container: true })
      // }

      this.wrapper.replaceChildren(...this.productSlides)
      // this.storedRecommendations = this.wrapper.innerHTML
      console.debug('Updating product recommendations for nosto placement ID:', this.nostoPlacementId)

      // if (!hasCachedRecommendations) {
      this.wrapper.style.display = this.isCartDrawer ? 'flex' : 'inline-flex'
      this.wrapper.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 300,
        fill: 'forwards',
        easing: 'ease-out',
      })
      // }
      this.initSlider()
      NostoHelper.triggerMigration(`#${this.id}`, true)
    }

    isSearchPage(url = window.location.href) {
      const urlObj = new URL(url)
      const hasQParam = urlObj.searchParams.has('q')
      const isSearchPath = urlObj.pathname.includes('/search')

      this.searchQuery = urlObj.searchParams.get('q')

      return hasQParam && isSearchPath
    }

    prepareProductResult(product) {
      if (product.prepared) return product

      product.title = product.name
      delete product.name

      product.tags = [...product.tags1, ...product.tags2, ...product.tags3]
      delete product.tags1
      delete product.tags2
      delete product.tags3

      product.handle = handle(product.title)
      product.variants = product.skus.map(variant => {
        const [size, color] = variant.name.split('|').map(part => part.trim())
        variant.size = size
        variant.color = color
        return variant
      })
      delete product.skus

      product.variant_id = product.variants.find(variant => variant.available).id

      product.vendor = product.brand
      delete product.brand

      product.collections = product.categories
      delete product.categories

      if (product.price < product.list_price) {
        product.on_sale = true
        // TODO: Handle price localization
        // product.price = formatPrice(product.price * 100)
        // product.list_price = formatPrice(product.list_price * 100)
      }

      const currency = window.Shopify?.currency
      // if (currency) {
      //   const rate = parseFloat(currency.rate)
      //   product.price = product.price * rate
      //   product.list_price = product.list_price * rate
      // product.price_currency_code = currency.active
      // }

      product.price = formatPrice(product.price * 100)
      product.list_price = formatPrice(product.list_price * 100)

      if (Shopify?.money_trailing_zeros === false) {
        product.price = product.price.replace('.00', '')
        product.list_price = product.list_price.replace('.00', '')
      }

      if (currency?.active != 'USD') {
        product.price = `${product.price_currency_code} ${product.price}`
        product.list_price = `${product.price_currency_code} ${product.list_price}`
      }

      product.prepared = true

      return product
    }

    toggleDisplay(show = true) {
      this.container.style.display = show ? 'block' : 'none'
    }

    async getRecommendedProducts() {
      if (this.nostoPlacementId && window.nostojs) {
        window.nostojs(api => {
          const defaultSession = api.defaultSession()

          const viewSession = this.isSearchPage()
            ? defaultSession.viewSearch(this.searchQuery)
            : defaultSession.viewProduct(`${this.productId}`)

          viewSession
            .setPlacements(this.nostoPlacementId ? [this.nostoPlacementId] : api.placements.getPlacements()) // If no placement ID present, dynamically fetch all available nosto placement/slot
            .load()
            .then(async response => {
              console.log('response', response)
              const { recommendations } = response.campaigns
              let products = {}
              let realSlotIdRendered = ''
              console.log('recommendations', recommendations)

              if (this.nostoPlacementId) {
                products = recommendations[this.nostoPlacementId].products
                realSlotIdRendered = recommendations[this.nostoPlacementId].result_id
              } else {
                for (const property in recommendations) {
                  if (
                    recommendations[property]['title'].includes('You Might Also Like') ||
                    recommendations[property]['title'].includes('who searched')
                  ) {
                    products = recommendations[property].products
                    realSlotIdRendered = recommendations[property].result_id
                    break
                  }
                }
              }

              products = products.map(this.prepareProductResult.bind(this))

              if (products.length > 0) {
                this.toggleDisplay(true)
                this.loopProducts(realSlotIdRendered, products)
              } else {
                this.toggleDisplay(false)
              }
            })
        })

        return
      }

      const productRecommendations = await getProductRecommendations({
        product_id: this.productId,
        limit: 1,
        intent: 'related',
      })

      if (productRecommendations.products.length === 0) return

      this.renderProductRecommendation(productRecommendations.products[0])
    }

    waitForNostoToLoad() {
      let limitInterval = 0
      const checkInterval = setInterval(() => {
        if (typeof window.nostojs === 'function' || limitInterval >= 10) {
          this.getRecommendedProducts()
          clearInterval(checkInterval)
        }

        limitInterval++
      }, 500)
    }

    initSlider() {
      let pagination = false
      if (this.querySelector('.swiper-pagination')) {
        pagination = {
          el: '.swiper-pagination',
          clickable: true,
        }
      }

      const swiper = new Swiper(this.querySelector('.product-recommendations-slider'), {
        modules: [A11y, Keyboard, Navigation, Pagination],
        a11y: {
          enabled: true,
          containerRole: 'group',
          containerRoleDescriptionMessage: 'carousel',
          itemRoleDescriptionMessage: 'slide',
          prevSlideMessage: 'Previous products',
          nextSlideMessage: 'Next products',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        watchSlidesProgress: true,
        spaceBetween: 0,
        slidesPerView: this.isCartDrawer ? 3 : 'auto',
        slidesPerGroup: this.isCartDrawer && this.groupSlides ? 3 : 1,
        breakpoints: {
          750: {
            slidesPerView: 3,
            slidesPerGroup: this.groupSlides ? 3 : 1,
          },
          990: {
            slidesPerView: this.isCartDrawer ? 3 : 4,
            slidesPerGroup: this.groupSlides ? (this.isCartDrawer ? 3 : 4) : 1,
          },
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination,
      })
      enhanceSwiperAccessibility(swiper)
    }
  }

  customElements.define('product-recommendations', ProductRecommendations)
}
